import {
  faExternalLinkSquareAlt,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { cad, github, gitlab, googlePlay } from "../data/frameworks";
import { Framework, ProjectModel } from "../models/projectModel";
import { ConvertMdFile } from "../utils/convertMdFile";
import { formatDate } from "../utils/formatDate";
import ImageFullSize from "./ImageFullSize";
import { ScaledImage } from "./scaledImage";

interface Props {
  project: ProjectModel;
}

export const ProjectWidget = ({ project }: Props) => {
  const [slide, setSlide] = useState<number>(0);
  const createdDate = formatDate(project.createdDate);

  const latestUpdateDate = formatDate(project.latestUpdate);

  return (
    <div className="sm:py-4 bg-gray-200 rounded-xl">
      <div className="inline-flex flex-col w-full gap-5">
        <div className="inline-flex flex-col lg:flex-row  gap-5 items-start">
          <div className="inline-flex flex-1 flex-col gap-3 lg:w-1/2">
            <div className="inline-flex flex-col">
              {buildDescription(project)}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
              {buildInfoText("Framework", "", project.framework)}
              {project.url ? buildLinks(project) : <></>}
            </div>

            {project.keywords.length >= 0 ? buildKeywords(project) : <></>}

            <div className="mt-6 text-italic font-medium inline-flex flex-col">
              {latestUpdateDate !== createdDate ? (
                <div className="inline-flex flex-row gap-2">
                  <span className={infoTitleStyle}>Latest Update: </span>
                  <span className={infoTextStyle}>{latestUpdateDate}</span>
                </div>
              ) : (
                <></>
              )}
              <div className="inline-flex flex-row gap-2">
                <span className={infoTitleStyle}>Created Date: </span>
                <span className={infoTextStyle}>{createdDate}</span>
              </div>
            </div>
          </div>
          <div className="inline-flex flex-1 flex-col items-center">
            {project.images &&
              project.images.length > 0 &&
              buildImage(project, slide)}

            {buildThumbnailsBar(project, setSlide, slide)}
          </div>
        </div>
      </div>
    </div>
  );
};

function buildDescription(project: ProjectModel) {
  return (
    <ConvertMdFile
      mdFile={
        project.longDescription ? project.longDescription : project.description
      }
    />
  );
}

function buildImage(project: ProjectModel, slide: number) {
  return (
    <div className="relative">
      <ScaledImage
        category={project.category}
        imagePath={project.images[slide]}
        alt={project.key}
        index={slide}
      />
      <ImageFullSize
        path={project.images[slide]}
        title={`${project.title} - ${Number(slide) + 1}`}
        category={project.category}
      />
    </div>
  );
}

function buildThumbnailsBar(
  project: ProjectModel,
  setSlide: React.Dispatch<React.SetStateAction<number>>,
  slide: number
) {
  return (
    <div className="inline-flex flex-row flex-wrap gap-4 justify-center my-4">
      {project.images &&
        project.images.length > 0 &&
        project.images.map((e, index) => (
          <div
            key={index}
            onClick={() => setSlide(index)}
            className={` ${
              slide === index
                ? "border-2 border-red-500"
                : "border-2 border-gray-700"
            } cursor-pointer`}
          >
            <ScaledImage
              category={project.category}
              imagePath={e}
              index={index}
              alt={project.key}
              isThumbnail={true}
            />
          </div>
        ))}
    </div>
  );
}

function buildKeywords(project: ProjectModel) {
  return (
    <div className="inline-flex flex-col">
      <span className={infoTitleStyle}>Keywords</span>
      <div
        className={
          project.keywords.length >= 4
            ? "grid grid-cols-2"
            : "inline-flex flex-col"
        }
      >
        {project.keywords.map((e) => (
          <div key={e} className="ml-6 list-item">
            <span className={infoTextStyle}>{e}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildLinks(project: ProjectModel) {
  return (
    <div className="inline-flex flex-col w-1/3">
      <span className={infoTitleStyle}>Links</span>
      {project.url &&
        project.url.map((e, index) => (
          <div key={index}>
            <a rel="noopener noreferrer" href={e} target="_blank">
              {e.includes("play.google") ? (
                <div className="my-1 h-8">
                  <img width={100}
                    alt={googlePlay.name}
                    src={`/assets/icons/${googlePlay.icon}`}
                  ></img>
                </div>
              ) : e.includes("github") ? (
                buildIconAndTextRow(github.icon, github.name)
              ) : e.includes("gitlab") ? (
                buildIconAndTextRow(gitlab.icon, gitlab.name)
              ): (
                buildIconAndTextRow(
                  "",
                  " Visit Site",
                  faExternalLinkSquareAlt,
                  false,
                  "text-indigo-900",
                  e
                )
              )}
            </a>
          </div>
        ))}
    </div>
  );
}

export function buildIconAndTextRow(
  icon: string,
  text: string,
  faIcon?: IconDefinition,
  isTextHide: boolean = false,
  color: string = "",
  subtitle?: string
) {
  if (faIcon) {
    return (
      <div className="inline-flex flex-row items-center gap-2 py-1 ">
        <span className={`text-3xl ${color}`}>
          <FontAwesomeIcon icon={faIcon} />
        </span>
        {subtitle ? (
          <div className="inline-flex flex-col">
            <span className={`text-lg font-medium ${color}`}>{text}</span>
            <span className={`text-xs  ${color}`}>{subtitle}</span>
          </div>
        ) : (
          <span className={`text-lg font-medium ${color}`}>{text}</span>
        )}
      </div>
    );
  } else {
    return (
      <div className=" inline-flex flex-row items-center gap-2">
        <img className="h-10" alt={text} src={`/assets/icons/${icon}`}></img>
        {isTextHide ? <></> : <span className={infoTextStyle}>{text}</span>}
      </div>
    );
  }
}

function buildInfoText(title: string, info: string, framework?: Framework) {
  const isTextHide: boolean = framework === cad ? true : false;
  return (
    <div className="inline-flex flex-col ">
      <span className={infoTitleStyle}>{title}</span>
      {framework ? (
        buildIconAndTextRow(
          framework.icon,
          framework.name,
          undefined,
          isTextHide
        )
      ) : (
        <span className={infoTextStyle}>{info}</span>
      )}
    </div>
  );
}

const infoTitleStyle = "text-lg font-medium underline";
const infoTextStyle = "text-lg ";
